import { useState } from 'react';
import { useKV } from '@/hooks/useLocalStorage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  Play,
  Target,
  X,
} from '@phosphor-icons/react';
import { Tutorial } from '@/lib/tutorialTypes';

interface TutorialOverlayProps {
  tutorials: Tutorial[];
  onTutorialComplete: (tutorialId: string) => void;
  onTabChange: (tab: string) => void;
}

export function TutorialOverlay({
  tutorials,
  onTutorialComplete,
  onTabChange,
}: Readonly<TutorialOverlayProps>) {
  const [, setCompletedTutorials] = useKV<string[]>('completed-tutorials', []);
  const [currentStep, setCurrentStep] = useState(0);

  const activeTutorial = tutorials.length > 0 ? tutorials[0] : null;

  const handleNextStep = () => {
    if (activeTutorial && currentStep < activeTutorial.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCompleteTutorial = () => {
    if (activeTutorial) {
      setCompletedTutorials(prev => [...prev, activeTutorial.id]);
      onTutorialComplete(activeTutorial.id);
      setCurrentStep(0);
    }
  };

  const handleCloseTutorial = () => {
    onTutorialComplete('');
    setCurrentStep(0);
  };

  const progress = activeTutorial
    ? ((currentStep + 1) / activeTutorial.steps.length) * 100
    : 0;

  if (!activeTutorial) {
    return null;
  }

  return (
    <>
      {/* Active Tutorial Modal */}
      <Dialog open={!!activeTutorial} onOpenChange={() => {}}>
        <DialogContent className='max-w-3xl max-h-[85vh] overflow-y-auto'>
          {activeTutorial && (
            <>
              <DialogHeader>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-3'>
                    <div className='p-2 rounded-lg bg-primary/10 text-primary'>
                      {activeTutorial.icon}
                    </div>
                    <div>
                      <DialogTitle className='text-xl'>
                        {activeTutorial.title}
                      </DialogTitle>
                      <DialogDescription>
                        Step {currentStep + 1} of {activeTutorial.steps.length}
                      </DialogDescription>
                    </div>
                  </div>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleCloseTutorial}
                    className='h-8 w-8 p-0'
                  >
                    <X className='w-4 h-4' />
                  </Button>
                </div>
                <Progress value={progress} className='mt-3' />
              </DialogHeader>

              <div className='space-y-6'>
                <div className='bg-muted/20 rounded-lg p-6'>
                  <h3 className='text-lg font-semibold mb-3'>
                    {activeTutorial.steps[currentStep].title}
                  </h3>
                  <p className='text-muted-foreground mb-4'>
                    {activeTutorial.steps[currentStep].description}
                  </p>
                  <div className='prose prose-sm max-w-none'>
                    {activeTutorial.steps[currentStep].content}
                  </div>
                </div>

                {activeTutorial.steps[currentStep].action && (
                  <Card className='border-accent/20 bg-accent/5'>
                    <CardContent className='p-4'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                          <Target className='w-4 h-4 text-accent' />
                          <span className='text-sm font-medium'>
                            Try it yourself:
                          </span>
                        </div>
                        <Button
                          onClick={
                            activeTutorial.steps[currentStep].action.callback
                          }
                          size='sm'
                          variant='outline'
                          className='gap-1'
                        >
                          <Play className='w-3 h-3' />
                          {activeTutorial.steps[currentStep].action.text}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className='flex items-center justify-between pt-4'>
                  <Button
                    variant='outline'
                    onClick={handlePrevStep}
                    disabled={currentStep === 0}
                    className='gap-1'
                  >
                    <CaretLeft className='w-4 h-4' />
                    Previous
                  </Button>

                  <div className='flex items-center gap-2'>
                    {Array.from(
                      { length: activeTutorial.steps.length },
                      (_, i) => (
                        <div
                          key={i}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            i <= currentStep ? 'bg-primary' : 'bg-muted'
                          }`}
                        />
                      )
                    )}
                  </div>

                  {currentStep < activeTutorial.steps.length - 1 ? (
                    <Button onClick={handleNextStep} className='gap-1'>
                      Next
                      <CaretRight className='w-4 h-4' />
                    </Button>
                  ) : (
                    <Button onClick={handleCompleteTutorial} className='gap-1'>
                      <CheckCircle className='w-4 h-4' />
                      Complete
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
